import { useEffect, useState } from "react";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { usePost } from "../../hooks/usePost";
import { REPORT_CLUB_CLOSURE } from "../../services/apis";

const CLOSURE_OPTIONS = [
  {
    value: "temporary",
    title: "Temporary Closed",
    description: "Club is closed for now and may reopen later.",
  },
  {
    value: "permanent",
    title: "Permanent Closed",
    description: "Club has shut down and is no longer operating.",
  },
];

const ReportClubClosure = ({
  clubId,
  clubName,
  isOpen,
  closeModal,
}) => {
  const [closureType, setClosureType] = useState("");
  const [note, setNote] = useState("");

  const { mutate, isPending } = usePost({
    url: `/${REPORT_CLUB_CLOSURE}`,
    onSuccess: (res) => {
      toast.success(res?.message || "Thanks! Your closure report was submitted.");
      closeModal();
    },
    onError: () => {
      // toast handled in usePost
    },
  });

  useEffect(() => {
    if (!isOpen) {
      setClosureType("");
      setNote("");
    }
  }, [isOpen]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!closureType) {
      toast.error("Please select permanent or temporary close.");
      return;
    }
    if (!clubId) {
      toast.error("Club not found.");
      return;
    }

    mutate({
      clubId,
      closureType,
      note: note.trim() || undefined,
      clubName: clubName || undefined,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="event-attendance-modal report-closure-modal"
      contentLabel="Report Club Closure"
      style={{
        overlay: {
          position: "fixed",
          zIndex: 99,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        },
      }}
    >
      <h2>Report Closure</h2>
      <button
        type="button"
        onClick={closeModal}
        className="modal__close-btn"
        aria-label="Close"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="size-6"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18 18 6M6 6l12 12"
          />
        </svg>
      </button>

      <p className="report-closure-modal__intro">
        {clubName
          ? `Tell us about the closure status of ${clubName}.`
          : "Tell us about the closure status of this club."}
      </p>

      <form onSubmit={handleSubmit}>
        <div className="report-closure-options" role="radiogroup" aria-label="Closure type">
          {CLOSURE_OPTIONS.map((option) => {
            const selected = closureType === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`report-closure-option${
                  selected ? " report-closure-option--selected" : ""
                }`}
                onClick={() => setClosureType(option.value)}
              >
                <span className="report-closure-option__radio" aria-hidden="true" />
                <span className="report-closure-option__text">
                  <strong>{option.title}</strong>
                  <span>{option.description}</span>
                </span>
              </button>
            );
          })}
        </div>

        <label className="report-closure-modal__note-label" htmlFor="closure-note">
          Additional details (optional)
        </label>
        <textarea
          id="closure-note"
          name="note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Closed for renovation / permanently shut"
        />

        <div className="report-closure-modal__actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeModal}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-dark"
            disabled={isPending || !closureType}
          >
            {isPending ? "Submitting..." : "Submit Report"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ReportClubClosure;
